/**
 * Stores, a.k.a. Syncables, are School Connect's "offline-provide-able" data.
 *
 * All stores have at least these attributes:
 * - `client_id`: FE-only, identifies stores
 * - `id`: used by the BE DB to identify stores
 * - `model`: used by the FE, but also by the BE to map to Django models
 * - `sync`: FE-only attribute, tells us whether a store needs syncing
 *
 */

import moment, { Moment, MomentInput } from "moment";
import {
  all,
  call,
  put,
  takeEvery,
  takeLatest,
  delay,
  race,
  take,
  select,
} from "redux-saga/effects";
import axios from "axios";
import { v4 } from "uuid";
import { createSelector } from "reselect";
import { persistReducer } from "redux-persist";
import localforage from "localforage";
import { toast } from "react-toastify";
import _ from "lodash";

import { RootState } from "data-handler/rootReducer";
import reportsV3Duck, {
  getIsReportLocked,
  getReportByMoment,
} from "data-handler/ducks/reportsV3";
import { getCurrentSchool } from "data-handler/ducks/schools";
import commoditiesDuck from "data-handler/ducks/commodities";
import deliveryIssuesDuck from "data-handler/ducks/deliveryIssues";
import incidentCausesDuck from "data-handler/ducks/incidentCauses";
import { REFRESH_LAST_ACTION_PERFORMED_AT } from "data-handler/ducks/auth";

import {
  buildNumber,
  deliveryCategory,
  studentAttendanceCategory,
  takeHomeRationCategory,
  toastFormattedMessages,
} from "SCConstants";

import { sortModels } from "helpers/stores";
import { getCanUseOfflineMode } from "helpers/users";
import { IdToNoMealReasonMapping } from "./noMealReasons";
import {
  processStoresForSync,
  processSyncResponse,
} from "data-handler/adapters";

import {
  Store,
  DeliveryEvent,
  PurchaseEvent,
  IncidentEvent,
  Attendance,
  EnrolmentUpdate,
  OfflineUser,
  SchoolYear,
  Report,
  NewStockEvent,
  AggregateSchoolYearLevel,
  AsyncSyncStatus,
  AsyncSyncState,
  SyncProgress,
  UserStores,
  AttendanceStore,
} from "data-handler/types/stores";

export * from "data-handler/types/stores";

export const CLEAR_STORES = "CLEAR_STORES";
export const UPDATE = "schoolconnect/stores/UPDATE";
export const REQUEST = "schoolconnect/stores/REQUEST";
export const SUCCESS = "schoolconnect/stores/SUCCESS";
export const FAILURE = "schoolconnect/stores/FAILURE";
export const POST_SUCCESS = "schoolconnect/stores/POST_SUCCESS";
export const RESET_SYNC = "schoolconnect/stores/RESET_SYNC";
export const USER_STORES_REQUEST = "schoolconnect/stores/USER_STORES_REQUEST";
export const USER_STORES_SUCCESS = "schoolconnect/stores/USER_STORES_SUCCESS";
export const USER_STORES_FAILURE = "schoolconnect/stores/USER_STORES_FAILURE";
export const USER_SCHOOL_IDS_REQUEST =
  "schoolconnect/stores/USER_SCHOOL_IDS_REQUEST";
export const USER_SCHOOL_IDS_SUCCESS =
  "schoolconnect/stores/USER_SCHOOL_IDS_SUCCESS";
export const USER_SCHOOL_IDS_FAILURE =
  "schoolconnect/stores/USER_SCHOOL_IDS_FAILURE";
export const USER_STORES_PROGRESS = "schoolconnect/stores/USER_STORES_PROGRESS";

export const SET_USER_CACHED_STORES =
  "schoolconnect/stores/SET_USER_CACHED_STORES";

export const ASYNC_SYNC_START = "schoolconnect/stores/ASYNC_SYNC_START";
export const ASYNC_SYNC_SUBMITTED = "schoolconnect/stores/ASYNC_SYNC_SUBMITTED";
export const ASYNC_SYNC_POLL = "schoolconnect/stores/ASYNC_SYNC_POLL";
export const ASYNC_SYNC_COMPLETE = "schoolconnect/stores/ASYNC_SYNC_COMPLETE";
export const ASYNC_SYNC_ERROR = "schoolconnect/stores/ASYNC_SYNC_ERROR";
export const ASYNC_SYNC_CANCEL = "schoolconnect/stores/ASYNC_SYNC_CANCEL";
export const ASYNC_SYNC_RESET = "schoolconnect/stores/ASYNC_SYNC_RESET";
export const PENDING_SYNCS_CHECKED =
  "schoolconnect/stores/PENDING_SYNCS_CHECKED";
export const CHECK_PENDING_SYNCS = "schoolconnect/stores/CHECK_PENDING_SYNCS";

type StoreModel = "attendance" | "enrolment" | "year" | "offlineuser";

// --- End StoreEvent types ---

export interface AttendanceStores {
  [occurredOn: string]: AttendanceStore;
}

export interface StoresState {
  changes: number;
  fetching: boolean;
  error: any;
  stores: Store[];
  userStores: UserStores;
  userStoresFetching: boolean;
  userSchoolIds: number[];
  userSchoolIdsFetching: boolean;
  userSchoolIdsError: any;
  userStoresProgress: {
    active: boolean;
    total: number;
    completed: number;
  };
  attendanceStores: AttendanceStores;
  storesPreEdit: Store[];
  POSTedStores: string[];
  asyncSync: AsyncSyncState;
}

export interface ActionState {
  type?: any;
  data?: any;
  client_id?: any;
  stores?: any;
  error?: any;
  response?: any;
  schoolId?: any;
  school?: any;
  user?: any;
  jobId?: string | null;
  payloadCount?: number;
  schoolIds?: number[];
  total?: number;
  completed?: number;
  increment?: number;
  progress?: SyncProgress | null;
}

export type StockMovementStore =
  | DeliveryEvent
  | PurchaseEvent
  | Attendance
  | IncidentEvent;
export type StockStore = StockMovementStore | SchoolYear;

export const isDeliveryStore = (store: Store): store is DeliveryEvent =>
  "event_type" in store && store.event_type === "delivery";
export const isAttendanceStore = (store: Store): store is Attendance =>
  store.model === "attendance";
export const isPurchaseDetailStore = (store: Store): store is PurchaseEvent =>
  "event_type" in store && store.event_type === "purchase_detail";
export const isIncidentStore = (store: Store): store is IncidentEvent =>
  "event_type" in store && store.event_type === "incident";
export const isSchoolYearStore = (store: Store): store is SchoolYear =>
  store.model === "year";
export const isReportStore = (store: Store): store is Report =>
  store.model === "report";
export const isEnrolmentUpdateStore = (
  store: Store
): store is EnrolmentUpdate => store.model === "enrolment";
export const isMealAttendanceStore = (store: Store): boolean =>
  isAttendanceStore(store) && store.category === deliveryCategory;
export const isStudentAttendanceStore = (store: Store): boolean =>
  isAttendanceStore(store) && store.category === studentAttendanceCategory;
export const isTakeHomeRationAttendanceStore = (store: Store): boolean =>
  isAttendanceStore(store) && store.category === takeHomeRationCategory;

export const isDeletedStore = (store: Store): boolean =>
  store.type === "delete";

export const isSyncedStore = (store: Store): boolean => store.sync === true;

export const isStockMovementStore = (
  store: Store
): store is StockMovementStore =>
  isDeliveryStore(store) ||
  isAttendanceStore(store) ||
  isPurchaseDetailStore(store) ||
  isIncidentStore(store);

export const isStockStore = (store: Store): store is StockStore =>
  isStockMovementStore(store) || isSchoolYearStore(store);

const nowOrLatestToday = (date: string) => {
  const occurredAtEndOfDay = moment(date, "YYYY-MM-DD").set({
    hour: 23,
    minute: 59,
    second: 59,
  });

  const now = moment();

  return occurredAtEndOfDay.isAfter(now)
    ? now.format("YYYY-MM-DDTHH:mm:ss") // If future, return current datetime
    : occurredAtEndOfDay.format("YYYY-MM-DDTHH:mm:ss"); // Otherwise, return 23:59:59
};

export const getStoreDate = (
  store: StockStore | EnrolmentUpdate,
  granular: boolean = false
) => {
  if (isDeliveryStore(store)) {
    return store.occurred_at;
  } else if (isAttendanceStore(store)) {
    return granular ? nowOrLatestToday(store.occurred_on) : store.occurred_on;
  } else if (isPurchaseDetailStore(store)) {
    const purchaseDate = store.occurred_at;
    return granular
      ? moment(purchaseDate, "YYYY-MM-DD")
          .set({
            hour: 0,
            minute: 0,
            second: 0,
          })
          .format("YYYY-MM-DDTHH:mm:ss")
      : purchaseDate;
  } else if (isIncidentStore(store)) {
    return granular ? nowOrLatestToday(store.occurred_at) : store.occurred_at;
  } else if (isSchoolYearStore(store)) {
    return store.starts_on;
  } else if (isEnrolmentUpdateStore(store)) {
    return store.occurred_on;
  }
};

export const getCommodities = (store: StockMovementStore): NewStockEvent[] => {
  let result: NewStockEvent[] | undefined;
  if (isAttendanceStore(store)) {
    result = store.consumption?.stock_events;
  } else if (isDeliveryStore(store)) {
    result = store.stock_events;
  } else if (isPurchaseDetailStore(store)) {
    result = store.stock_events;
  } else if (isIncidentStore(store)) {
    result = store.stock_events;
  }
  return result || [];
};

// compareFn functions:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#comparefn
export const compareByDate = (
  storeA: StockStore,
  storeB: StockStore
): number => {
  const dateA = getStoreDate(storeA);
  const dateB = getStoreDate(storeB);
  // dateA comes after dateB
  if (moment(dateA).isAfter(dateB, "days")) return 1;
  // dateB comes after dateA
  if (moment(dateA).isBefore(dateB, "days")) return -1;
  // They have same date.
  return 0;
};

export const compareByModel = (
  storeA: StockStore,
  storeB: StockStore
): number => {
  const isStockMovementAdditionA =
    isDeliveryStore(storeA) || isPurchaseDetailStore(storeA);
  const isStockMovementAdditionB =
    isDeliveryStore(storeB) || isPurchaseDetailStore(storeB);
  if (isStockMovementAdditionA && !isStockMovementAdditionB) return -1; // A come before B
  if (!isStockMovementAdditionA && isStockMovementAdditionB) return 1; // B come before A
  // They are equal
  return 0;
};

export const compareByDateAndModel = (
  storeA: StockStore,
  storeB: StockStore
): number => {
  const dateCompare = compareByDate(storeA, storeB);
  if (dateCompare !== 0) {
    return dateCompare;
  } else {
    return compareByModel(storeA, storeB);
  }
};

type CompareFn = (a: any, b: any) => number;

/**
 * Reverse given compare function
 */
export const reverseCompareFn = (compareFn: CompareFn): CompareFn => (
  a: any,
  b: any
) => compareFn(a, b) * -1;

export const initialAsyncSyncState: AsyncSyncState = {
  status: "idle",
  jobId: null,
  error: null,
  payloadCount: 0,
  pendingSyncsChecked: false,
  progress: null,
  schoolId: null,
};

export const initialState: StoresState = {
  changes: 0,
  fetching: false,
  error: null,
  /** Stores as they currently are in the client, including local edits */
  stores: [],
  /** User stores grouped by school id, to allow offline users to switch between schools */
  userStores: {},
  userStoresFetching: false,
  userSchoolIds: [],
  userSchoolIdsFetching: false,
  userSchoolIdsError: null,
  userStoresProgress: {
    active: false,
    total: 0,
    completed: 0,
  },
  /** Attendance preprocessed stores that are grouped by accored on date */
  attendanceStores: {},
  /** Stores at the time of the last sync operation */
  storesPreEdit: [],
  POSTedStores: [],
  asyncSync: initialAsyncSyncState,
};

const updateAttendanceStore = (
  attendanceStore: AttendanceStore,
  store: Store
) => {
  if (isStudentAttendanceStore(store)) {
    attendanceStore.studentAttendance = store;
  } else if (isMealAttendanceStore(store)) {
    attendanceStore.mealAttendance = store;
  } else if (isTakeHomeRationAttendanceStore(store)) {
    attendanceStore.thrAttendance = store;
  }
  return attendanceStore;
};

const makeDefaultAttendanceStore = (occurredOn: string): AttendanceStore => ({
  occurredOn,
  mealAttendance: undefined,
  studentAttendance: undefined,
  thrAttendance: undefined,
});

const makeAttendanceStores = (allStores: Store[]) => {
  const attendanceStores: AttendanceStores = {};

  for (let store of allStores) {
    if (isAttendanceStore(store)) {
      const occurredOn: string = store.occurred_on as string;
      const defaultAttendanceStore = makeDefaultAttendanceStore(occurredOn);

      const attendanceStore = updateAttendanceStore(
        attendanceStores[occurredOn] || defaultAttendanceStore,
        store
      );

      attendanceStores[occurredOn] = attendanceStore;
    }
  }

  return attendanceStores;
};

const reducer = (state = initialState, action: ActionState): StoresState => {
  switch (action.type) {
    case CLEAR_STORES:
      // Don't clear userStores (as it is used for offline capability)
      return {
        ...initialState,
        userStores: state.userStores,
        userStoresFetching: false,
        userSchoolIds: state.userSchoolIds,
        userSchoolIdsFetching: false,
        userSchoolIdsError: null,
        userStoresProgress: {
          active: false,
          total: 0,
          completed: 0,
        },
      };

    case REQUEST:
      return { ...state, fetching: true, error: null };

    case SUCCESS:
      let updatedStores = action.stores;
      const { user, schoolId } = action;
      // In the case where there are still some unsynced changes in the userStores for
      // the given school id, use these stores instead of the ones coming form the backend
      // This only applies to admin users with the offline capability
      if (getCanUseOfflineMode(user)) {
        const userSchoolStores = (state.userStores || {})[schoolId];
        const unsyncedUserSchoolStores = filterUnsyncedStores(userSchoolStores);

        if (unsyncedUserSchoolStores.length > 0) {
          updatedStores = userSchoolStores;
        }
      }

      return {
        ...state,
        fetching: false,
        error: null,
        stores: updatedStores,
        storesPreEdit: updatedStores,
        attendanceStores: makeAttendanceStores(updatedStores),
      };

    case FAILURE:
      // Only clear stores if the failed request was for a different school
      // than the currently stored stores (to prevent clearing on temporary errors
      // for the same school, but clear stale data when switching schools)
      const currentSchool = state.stores[0]?.school;
      const shouldClearStores =
        action.schoolId && currentSchool && action.schoolId !== currentSchool;

      return {
        ...state,
        fetching: false,
        error: action.error,
        ...(shouldClearStores
          ? {
              stores: [],
              storesPreEdit: [],
              attendanceStores: {},
            }
          : {}),
      };

    case UPDATE:
      const { id, model, values, type, section, category } = action.data;
      /** Index of the provided store (action.data) if updated, -1 if created */
      const preexistentStoreIndex = state.stores.findIndex(
        (store: any) => store.client_id === id
      );

      const isUpdate = preexistentStoreIndex !== -1;

      if (
        ("occurred_on" in values &&
          moment(values.occurred_on).isBefore(
            moment().format("YYYY-MM-DD"),
            "month"
          )) ||
        ("occurred_at" in values &&
          moment(values.occurred_at).isBefore(
            moment().format("YYYY-MM-DD"),
            "month"
          )) ||
        ("starts_on" in values &&
          moment(values.starts_on).isBefore(
            moment().format("YYYY-MM-DD"),
            "month"
          ))
      ) {
        toast.warning(
          toastFormattedMessages.find(
            (e) => e.name === "toast.dataEnteredForPreviousMonths"
          )?.label,
          {
            toastId: "previousMonthsSyncs",
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
          }
        );
      }

      /**
       * When "deleting" individual attendance or consumption we can just update them to nothing.
       * it is not a good idea to try and delete individual objects from the "dailyReport" object.
       * This is why we switch type from "delete" to "update" in these situations
       */
      let newValues = { ...values };
      let newType = type;
      if (type === "delete" && category !== takeHomeRationCategory) {
        if (section === "attendance") {
          delete newValues.levels;
          newType = "update";
        } else if (section === "consumption") {
          delete newValues.consumption;
          newType = "update";
        }
      } else if (
        type === "update" &&
        section !== undefined &&
        values.type === "delete"
      ) {
        // When creating a section while the other one is deleted (but not synced)
        // we need to create the new object without the deleted section
        if (section === "attendance") {
          delete newValues.consumption;
        } else if (section === "consumption") {
          delete newValues.levels;
        }
      }

      // copy attendance stores
      const newAttendanceStores = JSON.parse(
        JSON.stringify(state.attendanceStores)
      );

      const newStore = {
        ...newValues,
        // Preserve `id`: the BE DB id.
        // BE needs it to discern between update / create when syncing
        id: isUpdate ? state.stores[preexistentStoreIndex].id : undefined,
        last_edit: moment().toISOString(),
        client_id: id, // FE-generated id
        build_number: buildNumber,
        model,
        sync: false,
        type: newType,
        category,
      };

      // Create a new stores array
      // (never mutate redux state in-place unless you want to break reactivity)
      let newStoresArray: Store[];
      if (isUpdate) {
        if (
          state.stores[preexistentStoreIndex].sync === false &&
          newType === "delete" &&
          preexistentStoreIndex === -1
        ) {
          newStoresArray = [...state.stores].filter(
            (_, index) => index !== preexistentStoreIndex
          );
        } else {
          newStoresArray = [...state.stores];
          newStoresArray[preexistentStoreIndex] = newStore;
        }
      } else {
        newStoresArray = [...state.stores, newStore];
      }

      // If model is an attendance model update

      const occurredOn = newStore.occurred_on;
      //Add current store to newAttendanceStores;
      const attendanceStore: AttendanceStore =
        newAttendanceStores[occurredOn] ||
        makeDefaultAttendanceStore(occurredOn);
      newAttendanceStores[occurredOn] = updateAttendanceStore(
        attendanceStore,
        newStore
      );

      const userStoresChanged = getUserStoresChanged(
        state,
        newStoresArray,
        action
      );

      return {
        ...state,
        changes: state.changes + 1,
        stores: newStoresArray,
        attendanceStores: newAttendanceStores,
        ...userStoresChanged,
      };

    case POST_SUCCESS:
      // Create copies to preserve immutability
      const stores = [...state.stores];
      const storesPreEdit = [...(state.storesPreEdit || [])];

      // Key concept: the response preserves the order of stores of the request.
      // so we iterate over the response's data,
      // knowing that response.data.data[i] is the same syncable as POSTedStores[i].
      // For background sync, the response includes client_id, so we match on that directly.
      action.response.data.data.forEach(
        (
          respStore: {
            client_id?: string;
            error: any;
            id: string;
            error_details: any;
          },
          i: number
        ) => {
          // Match by client_id from response if available, otherwise use POSTedStores order
          const preexistentStoreIndex: any = respStore.client_id
            ? stores.findIndex(
                (store: Store) => store.client_id === respStore.client_id
              )
            : stores.findIndex(
                (store: Store) => store.client_id === state.POSTedStores[i]
              );

          // If this store was successfully synced
          if (!respStore.error) {
            // Drop this store's locally stored errors
            const { error, errorDetails, ...shallowCopy } = stores[
              preexistentStoreIndex
            ];

            // Update this store (TODO: use all of the response, not just `id`?)
            const updatedStore = {
              ...shallowCopy,
              id: respStore.id,
              sync: true,
              error: undefined,
              errorDetails: undefined,
            };
            stores[preexistentStoreIndex] = updatedStore;

            // Also update this store in storesPreEdit
            const indexInPreEdit = storesPreEdit.findIndex(
              (store) => store.client_id === shallowCopy.client_id
            );
            if (indexInPreEdit === -1) {
              storesPreEdit.push(updatedStore);
            } else {
              storesPreEdit[indexInPreEdit] = updatedStore;
            }
          } else {
            // Add errors from the BE to this store
            stores[preexistentStoreIndex] = {
              ...stores[preexistentStoreIndex],
              error: respStore.error,
              errorDetails: respStore.error_details,
            };
          }
        }
      );

      const newUserStores = getUserStoresChanged(state, stores, action);

      return {
        ...state,
        fetching: false,
        error: null,
        stores,
        storesPreEdit,
        ...newUserStores,
      };

    case RESET_SYNC:
      const { client_id } = action;
      const indexToDrop = state.stores.findIndex(
        (store: any) => store.client_id === client_id
      );
      if (indexToDrop === -1) {
        // The store you wanted to reset doesn't exist
        return state;
      }

      // Keep all stores except the one to reset
      const storesAfterDropping = state.stores.filter(
        (store: any) => store.client_id !== client_id
      );
      const resettedStore = (state.storesPreEdit || []).find(
        (store: any) => store.client_id === client_id
      );
      if (resettedStore) {
        // Re-add the dropped store as it was just after syncing
        storesAfterDropping.push(resettedStore);
      }

      const userStoresUpdated = getUserStoresChanged(
        state,
        storesAfterDropping,
        action
      );

      return {
        ...state,
        stores: storesAfterDropping,
        attendanceStores: makeAttendanceStores(storesAfterDropping),
        ...userStoresUpdated,
      };

    case USER_STORES_REQUEST:
      return {
        ...state,
        userStoresFetching: true,
        error: null,
        userStoresProgress: {
          active: true,
          total: 0,
          completed: 0,
        },
      };

    case USER_STORES_SUCCESS:
      // We only need to overwrite the user stores that don't have any unsynced items
      let filteredUserStores: UserStores = {};
      Object.keys(state.userStores || {}).forEach((key: any) => {
        const keyUserStores = state.userStores[key];
        if (filterUnsyncedStores(keyUserStores).length > 0) {
          filteredUserStores[key] = keyUserStores;
        }
      });

      return {
        ...state,
        userStoresFetching: false,
        error: null,
        userStores: { ...action.stores, ...filteredUserStores },
        userStoresProgress: {
          active: false,
          total: 0,
          completed: 0,
        },
      };

    case USER_STORES_FAILURE:
      return {
        ...state,
        userStoresFetching: false,
        error: action.error,
        userStoresProgress: {
          active: false,
          total: 0,
          completed: 0,
        },
      };
    case USER_STORES_PROGRESS: {
      const { total, increment, completed } = action;
      const nextTotal = total ?? state.userStoresProgress.total;
      let nextCompleted = state.userStoresProgress.completed;
      if (typeof completed === "number") {
        nextCompleted = completed;
      } else if (typeof increment === "number") {
        nextCompleted = Math.min(
          nextTotal,
          state.userStoresProgress.completed + increment
        );
      }
      return {
        ...state,
        userStoresProgress: {
          active: nextTotal === 0 ? true : nextCompleted < nextTotal,
          total: nextTotal,
          completed: nextCompleted,
        },
      };
    }
    case USER_SCHOOL_IDS_REQUEST:
      return {
        ...state,
        userSchoolIdsFetching: true,
        userSchoolIdsError: null,
      };
    case USER_SCHOOL_IDS_SUCCESS:
      return {
        ...state,
        userSchoolIdsFetching: false,
        userSchoolIdsError: null,
        userSchoolIds: action.schoolIds || [],
      };
    case USER_SCHOOL_IDS_FAILURE:
      return {
        ...state,
        userSchoolIdsFetching: false,
        userSchoolIdsError: action.error,
      };
    case SET_USER_CACHED_STORES:
      const cachedStores = [...(state.userStores[action.schoolId] || [])];
      return {
        ...state,
        stores: cachedStores,
        attendanceStores: makeAttendanceStores(cachedStores),
      };

    case ASYNC_SYNC_START:
      return {
        ...state,
        asyncSync: {
          status: "submitting",
          jobId: null,
          error: null,
          payloadCount: action.payloadCount || 0,
          pendingSyncsChecked: true,
          progress: null,
          schoolId: action.schoolId || null,
        },
        POSTedStores: action.stores
          ? action.stores.map((store: Store) => store.client_id)
          : state.POSTedStores,
      };

    case ASYNC_SYNC_SUBMITTED:
      return {
        ...state,
        asyncSync: {
          ...state.asyncSync,
          status: "polling",
          jobId: action.jobId ?? null,
        },
      };

    case ASYNC_SYNC_POLL:
      return {
        ...state,
        asyncSync: {
          ...state.asyncSync,
          status: "polling",
          progress: action.progress ?? state.asyncSync.progress,
        },
      };

    case ASYNC_SYNC_COMPLETE:
      // Check if there's a deferred service worker update waiting
      // (Set when update detected while unsynced data existed)
      // applyPendingUpdate is a function defined in index.tsx that checks
      // the pendingUpdate localStorage flag and applies the update if present
      window.applyPendingUpdate?.();

      return {
        ...state,
        asyncSync: {
          ...state.asyncSync,
          status: "completed",
          error: null,
        },
      };

    case ASYNC_SYNC_ERROR:
      return {
        ...state,
        asyncSync: {
          ...state.asyncSync,
          status: "error",
          error: action.error,
        },
      };

    case ASYNC_SYNC_CANCEL:
      return {
        ...state,
        asyncSync: {
          ...state.asyncSync,
          status: "cancelled",
        },
      };

    case ASYNC_SYNC_RESET:
      return {
        ...state,
        asyncSync: {
          ...initialAsyncSyncState,
          pendingSyncsChecked: state.asyncSync?.pendingSyncsChecked ?? false,
        },
      };

    case PENDING_SYNCS_CHECKED:
      return {
        ...state,
        asyncSync: {
          ...state.asyncSync,
          pendingSyncsChecked: true,
        },
      };

    case CHECK_PENDING_SYNCS:
      return {
        ...state,
        asyncSync: {
          ...initialAsyncSyncState,
          pendingSyncsChecked: false,
        },
      };

    default:
      return state;
  }
};

const persistedReducer = persistReducer(
  {
    key: "stores",
    storage: localforage,
    blacklist: ["changes", "error", "fetching", "asyncSync"],
  },
  reducer
);

export default persistedReducer;

type NormalizedRequestError = {
  name?: string;
  message: string;
  code?: string;
  isAxiosError?: boolean;
  status?: number;
  response?: {
    status?: number;
    data?: unknown;
  };
  config?: {
    url?: string;
    method?: string;
    baseURL?: string;
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const asString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const asNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

type AxiosErrorLike = {
  isAxiosError?: boolean;
  message?: unknown;
  code?: unknown;
  response?: unknown;
  config?: unknown;
};

const isAxiosErrorLike = (value: unknown): value is AxiosErrorLike => {
  if (!isRecord(value)) return false;
  const isAxios = value["isAxiosError"] === true;
  const hasConfigAndMessage = "config" in value && "message" in value;
  return isAxios || hasConfigAndMessage;
};

const normalizeRequestError = (error: unknown): NormalizedRequestError => {
  if (isAxiosErrorLike(error)) {
    const errorRecord: Record<string, unknown> = isRecord(error) ? error : {};
    const responseRecord = isRecord(errorRecord.response)
      ? errorRecord.response
      : undefined;
    const configRecord = isRecord(errorRecord.config)
      ? errorRecord.config
      : undefined;

    let status = responseRecord
      ? asNumber(responseRecord["status"])
      : undefined;

    if (!status && errorRecord["status"]) {
      status = asNumber(errorRecord["status"]);
    }

    const data = responseRecord ? responseRecord["data"] : undefined;

    return {
      message: asString(errorRecord.message) || "Request failed",
      code: asString(errorRecord.code),
      isAxiosError: errorRecord["isAxiosError"] === true,
      status,
      response: responseRecord ? { status, data } : undefined,
      config: configRecord
        ? {
            url: asString(configRecord["url"]),
            method: asString(configRecord["method"]),
            baseURL: asString(configRecord["baseURL"]),
          }
        : undefined,
    };
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message || "Unexpected error",
    };
  }

  return {
    message:
      typeof error === "string"
        ? error
        : error
        ? JSON.stringify(error)
        : "Unexpected error",
  };
};

export function* storesSaga() {
  yield takeEvery(REQUEST, workerGetStoresSaga);
}

function fetchGetStores(schoolId: number | string) {
  const url = `${process.env.REACT_APP_API_URL}/schools/${schoolId}/sync`.replace(
    "/v2/",
    "/v3/"
  );

  return axios({
    method: "GET",
    url: url,
  });
}

function* workerGetStoresSaga(action: {
  schoolId: number | string;
  type: typeof REQUEST;
}): any {
  try {
    const response = yield call(fetchGetStores, action.schoolId);

    // Use adapter to process response that might contain StoreEvents
    const stores = processSyncResponse(response.data);

    yield put({ type: SUCCESS, stores, schoolId: action.schoolId });
  } catch (error) {
    yield put({
      type: FAILURE,
      error: normalizeRequestError(error),
      schoolId: action.schoolId,
    });
  }
}

export function* updateStoreSagaWatcher() {
  yield takeEvery(UPDATE, updateStoreSagaWorker);
}

function* updateStoreSagaWorker() {
  // Saga worker to update the last performed action attribute of the auth store
  yield put({
    type: REFRESH_LAST_ACTION_PERFORMED_AT,
    data: moment().toString(),
  });
}

export function* userStoresSagaWatcher() {
  yield takeLatest(USER_STORES_REQUEST, userStoresSagaWorker);
}

export function* userStoresSagaWorker(): any {
  try {
    const schoolIdsResponse = yield call(fetchUserSchoolIds);
    const schoolIds = Array.isArray(schoolIdsResponse?.data?.school_ids)
      ? schoolIdsResponse.data.school_ids.filter(
          (id: unknown) => typeof id === "number"
        )
      : [];

    if (!schoolIds.length) {
      yield put({ type: USER_STORES_SUCCESS, stores: {} });
      return;
    }

    const storesBySchool: { [key: number]: Store[] } = {};
    yield put({
      type: USER_STORES_PROGRESS,
      total: schoolIds.length,
      completed: 0,
    });

    const fetchSchoolStores = function* (schoolId: number) {
      const response: { data: any } = yield call(fetchGetStores, schoolId);
      const processedStores = processSyncResponse(response.data);

      if (!storesBySchool[schoolId]) {
        storesBySchool[schoolId] = [];
      }

      processedStores.forEach((store: Store) => {
        const targetSchoolId = store.school ?? schoolId;
        if (!storesBySchool[targetSchoolId]) {
          storesBySchool[targetSchoolId] = [store];
        } else {
          storesBySchool[targetSchoolId].push(store);
        }
      });

      yield put({ type: USER_STORES_PROGRESS, increment: 1 });
    };

    // Fetch per-school syncs in parallel to avoid request timeout
    yield all(
      schoolIds.map((schoolId: number) => call(fetchSchoolStores, schoolId))
    );

    yield put({ type: USER_STORES_SUCCESS, stores: storesBySchool });
  } catch (error) {
    yield put({
      type: USER_STORES_FAILURE,
      error: normalizeRequestError(error),
    });
  }
}

export function* userSchoolIdsSagaWatcher() {
  yield takeLatest(USER_SCHOOL_IDS_REQUEST, userSchoolIdsSagaWorker);
}

function fetchUserSchoolIds() {
  const url = `${process.env.REACT_APP_API_URL}/user/school-ids`.replace(
    "/v2/",
    "/v3/"
  );

  return axios({
    method: "GET",
    url: url,
  });
}

export function* userSchoolIdsSagaWorker(): any {
  try {
    const response = yield call(fetchUserSchoolIds);
    const schoolIds = Array.isArray(response?.data?.school_ids)
      ? response.data.school_ids.filter((id: unknown) => typeof id === "number")
      : [];

    yield put({ type: USER_SCHOOL_IDS_SUCCESS, schoolIds });
  } catch (error) {
    yield put({
      type: USER_SCHOOL_IDS_FAILURE,
      error: normalizeRequestError(error),
    });
  }
}

const POLL_INTERVAL = 2000;

interface SyncJobResponse {
  job_id: string;
  status: string;
  payload_count: number;
}

interface SyncJobStatusResponse {
  job_id: string;
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "ERROR";
  payload_count: number;
  error_message?: string;
  progress?: SyncProgress | null;
  data?: Array<{
    client_id?: string;
    id?: string;
    error?: string;
    error_details?: any;
  }>;
}

interface PendingJobsResponse {
  pending_jobs: Array<{
    id: string;
    status: string;
    payload_count: number;
    date_created: string;
  }>;
}

function postAsyncSyncRequest({
  stores,
  schoolId,
}: {
  stores: Store[];
  schoolId: number;
}): Promise<{ data: SyncJobResponse }> {
  const reshapedStores = processStoresForSync(stores);

  return axios({
    method: "POST",
    url: `${process.env.REACT_APP_API_URL}/schools/${schoolId}/sync/background`.replace(
      "/v2/",
      "/v3/"
    ),
    data: { data: reshapedStores },
  });
}

function fetchSyncJobStatus(
  schoolId: number,
  jobId: string
): Promise<{ data: SyncJobStatusResponse }> {
  return axios({
    method: "GET",
    url: `${process.env.REACT_APP_API_URL}/schools/${schoolId}/sync/status/${jobId}`.replace(
      "/v2/",
      "/v3/"
    ),
  });
}

function fetchPendingJobs(
  schoolId: number
): Promise<{ data: PendingJobsResponse }> {
  return axios({
    method: "GET",
    url: `${process.env.REACT_APP_API_URL}/schools/${schoolId}/sync/pending`.replace(
      "/v2/",
      "/v3/"
    ),
  });
}

function* pollSyncStatus(schoolId: number, jobId: string): any {
  while (true) {
    yield delay(POLL_INTERVAL);

    try {
      // Check if user switched schools before even fetching status
      const currentSchool = yield select(getCurrentSchool);
      const currentSchoolId = currentSchool?.id;

      if (currentSchoolId?.toString() !== schoolId.toString()) {
        yield put({ type: ASYNC_SYNC_CANCEL });
        return { cancelled: true };
      }

      const response = yield call(fetchSyncJobStatus, schoolId, jobId);
      const { status, error_message, progress, data } = response.data;

      yield put({ type: ASYNC_SYNC_POLL, progress });

      if (status === "COMPLETED") {
        if (data && data.length > 0) {
          yield put({ type: POST_SUCCESS, response });
        }

        const hasErrors = data && data.some((item: any) => item.error);
        if (!hasErrors) {
          yield put(GETStores(schoolId));
        }

        yield put({ type: ASYNC_SYNC_COMPLETE });
        yield all([
          put(commoditiesDuck.fetchList()),
          put(incidentCausesDuck.fetchList()),
          put(deliveryIssuesDuck.fetchList()),
          put(reportsV3Duck.fetchList("DEFAULT", { school: schoolId })),
        ]);
        return { completed: true };
      }

      if (status === "ERROR") {
        yield put({
          type: ASYNC_SYNC_ERROR,
          error: error_message || "Sync failed",
        });
        return { error: error_message };
      }
    } catch (error) {
      yield put({
        type: ASYNC_SYNC_ERROR,
        error: normalizeRequestError(error).message,
      });
      return { error: "Failed to check sync status" };
    }
  }
}

function* asyncSyncWorker(action: {
  type: typeof ASYNC_SYNC_START;
  stores: Store[];
  schoolId: number;
}): any {
  try {
    const response = yield call(postAsyncSyncRequest, {
      stores: sortModels(action.stores),
      schoolId: action.schoolId,
    });

    const { job_id } = response.data;
    yield put({ type: ASYNC_SYNC_SUBMITTED, jobId: job_id });

    const { cancelled } = yield race({
      poll: call(pollSyncStatus, action.schoolId, job_id),
      cancelled: take(ASYNC_SYNC_CANCEL),
    });

    if (cancelled) {
      return;
    }
  } catch (error) {
    yield put({
      type: ASYNC_SYNC_ERROR,
      error: normalizeRequestError(error).message,
    });
  }
}

export function* asyncSyncWatcher() {
  yield takeLatest(ASYNC_SYNC_START, asyncSyncWorker);
}

export function* checkPendingSyncsWorker(action: {
  type: string;
  schoolId: number;
}): any {
  try {
    const response = yield call(fetchPendingJobs, action.schoolId);
    const { pending_jobs } = response.data;

    if (pending_jobs && pending_jobs.length > 0) {
      const job = pending_jobs[0];
      yield put({ type: ASYNC_SYNC_SUBMITTED, jobId: job.id });
      yield put({ type: PENDING_SYNCS_CHECKED });

      yield race({
        poll: call(pollSyncStatus, action.schoolId, job.id),
        cancelled: take(ASYNC_SYNC_CANCEL),
      });
    } else {
      yield put({ type: PENDING_SYNCS_CHECKED });
    }
  } catch (error) {
    // still mark as checked even on error
    yield put({ type: PENDING_SYNCS_CHECKED });
  }
}

export function* checkPendingSyncsWatcher() {
  yield takeLatest(CHECK_PENDING_SYNCS, checkPendingSyncsWorker);
}

//
// ACTION CREATORS
//

/** Requests all of the provided school's stores from the BE */
export const GETStores = (schoolId: any) => ({ type: REQUEST, schoolId });

/** Drops all stores */
export const clearStores = () => ({
  type: CLEAR_STORES,
});

/** Resets a store to its state as of its latest sync (drops it if never synced) */
export const resetStore = (
  client_id: string
): {
  type: string;
  client_id: string;
  blocked?: boolean;
} => ({
  type: RESET_SYNC,
  client_id,
});

/**
 * Updates or creates a syncable.
 *
 * Generates an UUIDv4 `client_id` for it if not provided.
 */
export const updateStore = ({
  id,
  model,
  values,
  type,
  section,
  category,
}: {
  id: string | undefined;
  model?: StoreModel;
  values: any;
  type: string;
  section: string;
  category: string;
}): {
  type: string;
  data: {
    id: string;
    values: any;
    model?: StoreModel;
    type: string;
    section: string;
    category: string;
  };
  blocked?: boolean;
} => ({
  type: UPDATE,
  data: {
    id: id || v4(),
    values,
    model,
    type,
    section,
    category,
  },
});

/** Requests the stores for all of the user's schools */
export const requestUserStores = () => ({ type: USER_STORES_REQUEST });

/** Requests the school ids the user has access to */
export const requestUserSchoolIds = () => ({ type: USER_SCHOOL_IDS_REQUEST });

export const setUserCachedStores = (schoolId: number) => ({
  type: SET_USER_CACHED_STORES,
  schoolId,
});

export const asyncSyncStart = (stores: Store[], schoolId: number) => ({
  type: ASYNC_SYNC_START,
  stores,
  schoolId,
  payloadCount: stores.length,
});

export const asyncSyncSubmitted = (jobId: string) => ({
  type: ASYNC_SYNC_SUBMITTED,
  jobId,
});

export const asyncSyncComplete = () => ({
  type: ASYNC_SYNC_COMPLETE,
});

export const asyncSyncError = (error: string) => ({
  type: ASYNC_SYNC_ERROR,
  error,
});

export const asyncSyncCancel = () => ({
  type: ASYNC_SYNC_CANCEL,
});

export const asyncSyncReset = () => ({
  type: ASYNC_SYNC_RESET,
});

export const checkPendingSyncs = (schoolId: number) => ({
  type: CHECK_PENDING_SYNCS,
  schoolId,
});

//
// SELECTORS
//

const selectStoresState = (state: RootState) =>
  (state && (state as any).stores) || initialState;

export const getIsStoresFetching = (state: RootState) =>
  selectStoresState(state).fetching;

export const getStoresError = (state: RootState) =>
  selectStoresState(state).error;

export const getUserSchoolIds = (state: RootState) =>
  selectStoresState(state).userSchoolIds;

export const getUserSchoolIdsFetching = (state: RootState) =>
  selectStoresState(state).userSchoolIdsFetching;

export const getUserSchoolIdsError = (state: RootState) =>
  selectStoresState(state).userSchoolIdsError;

export const getUserStoresProgress = (state: RootState) =>
  selectStoresState(state).userStoresProgress ||
  initialState.userStoresProgress;

export const getIsUserStoresFetching = (state: RootState) =>
  selectStoresState(state).userStoresFetching;

export const getAsyncSync = (state: RootState): AsyncSyncState =>
  selectStoresState(state).asyncSync;

export const getAsyncSyncStatus = (state: RootState): AsyncSyncStatus =>
  selectStoresState(state).asyncSync?.status || "idle";

export const getAsyncSyncJobId = (state: RootState): string | null =>
  selectStoresState(state).asyncSync?.jobId || null;

export const getAsyncSyncError = (state: RootState): string | null =>
  selectStoresState(state).asyncSync?.error || null;

export const getIsAsyncSyncInProgress = (state: RootState): boolean => {
  const status = getAsyncSyncStatus(state);
  return status === "submitting" || status === "polling";
};

export const getAsyncSyncProgress = (state: RootState): SyncProgress | null =>
  selectStoresState(state).asyncSync?.progress || null;

export const getIsPendingSyncsChecked = (state: RootState): boolean =>
  state.stores.asyncSync?.pendingSyncsChecked ?? false;

/** Returns all stores (School Connect's "syncable stuff") */
export const getAllStores = (state: RootState) => state?.stores?.stores ?? [];

/** Returns all unsynced stores (School Connect's "syncable stuff") */
export const getUnsyncedStores = createSelector([getAllStores], (stores) =>
  filterUnsyncedStores(stores)
);

/** Returns a selector */
export const getStoreByClientId = (clientId: string) => (state: RootState) =>
  getAllStores(state).find((store: Store) => store.client_id === clientId) ||
  null;

/** Returns a selector */
export const getDeliveryStoreByClientId = (clientId: string) => (
  state: RootState
) =>
  getAllStores(state).find((store: Store) => store.client_id === clientId) ||
  null;

export const getAllAttendances = createSelector(
  [getAllStores],
  (stores): Attendance[] =>
    (stores?.filter(
      (store: Store) => store.model === "attendance"
    ) as Attendance[]) || []
);

export const getAttendancesByDateRange = (date0: string, date1: string) => (
  state: RootState
) =>
  getAllAttendances(state).filter((store: { occurred_on: string }) =>
    moment(store.occurred_on).isBetween(date0, date1, "day", "[]")
  );

export const getAllDeliveriesAndPurchases = createSelector(
  [getAllStores],
  (stores) =>
    stores.filter(
      (store: Store) =>
        (isDeliveryStore(store) || isPurchaseDetailStore(store)) &&
        !isDeletedStore(store)
    ) as (DeliveryEvent | PurchaseEvent)[]
);

export const getAllDeliveriesAndPurchasesByDateRange = (
  date0: string,
  date1: string
) => (state: RootState) =>
  getAllDeliveriesAndPurchases(state).filter((store) =>
    moment(store.occurred_at).isBetween(date0, date1, "day", "[]")
  );

//TO-DO: Need to cover "mixed"
export const getAllDeliveries = createSelector(
  [getAllStores],
  (stores) =>
    stores.filter(
      (store: Store) =>
        isDeliveryStore(store) &&
        store.type !== "delete" &&
        store.category !== "takehomeration"
    ) as DeliveryEvent[]
);

export const getAllTakeHomeRationsDeliveries = createSelector(
  [getAllStores],
  (stores) =>
    stores.filter(
      (store: Store) => isDeliveryStore(store) && store.category !== "delivery"
    ) as DeliveryEvent[]
);

export const getDeliveryAttendancesByDateRange = (
  date0: string,
  date1: string
) =>
  createSelector([getAttendanceStores], (stores) =>
    stores.filter((store: { occurred_on: string }) =>
      moment(store.occurred_on).isBetween(date0, date1, "day", "[]")
    )
  );

export const getStudentAttendancesByDateRange = (
  date0: string,
  date1: string
) =>
  createSelector([getStudentAttendanceStores], (stores) =>
    stores.filter((store: { occurred_on: string }) =>
      moment(store.occurred_on).isBetween(date0, date1, "day", "[]")
    )
  );

export const getAttendanceStores = createSelector(
  [getAllAttendances],
  (allAttendances) =>
    allAttendances.filter(
      (store: { category: string }) => store.category === deliveryCategory
    ) as Attendance[]
);

export const getStudentAttendanceStores = createSelector(
  [getAllAttendances],
  (allAttendances) =>
    allAttendances.filter(
      (store: { category: string }) =>
        store.category === studentAttendanceCategory
    ) as Attendance[]
);

export const getThrAttendanceStores = createSelector(
  [getAllAttendances],
  (allAttendances) =>
    allAttendances.filter(
      (store: { category: string }) => store.category === takeHomeRationCategory
    ) as Attendance[]
);

export const getThrAttendanceStoresByDate = (date: string) => (
  state: RootState
) =>
  getAllAttendances(state).filter(
    (store: { category: string; occurred_on: string }) =>
      moment(store.occurred_on).isSame(date, "days") &&
      store.category === takeHomeRationCategory
  ) as Attendance[];

export const getThrAttendanceStoresByDateRange = (
  date0: string,
  date1: string
) => (state: RootState) =>
  getThrAttendanceStores(state).filter((store: { occurred_on: string }) =>
    moment(store.occurred_on).isBetween(date0, date1, "day", "[]")
  ) as Attendance[];

export const getAllAttendanceStoresByDay = (
  state: RootState
): AttendanceStores => state.stores.attendanceStores;

export const getAllAttendanceStoresForDay = (date: string) => (
  state: RootState
): AttendanceStore | undefined => state.stores.attendanceStores[date as string];

export const getAllPurchases = createSelector(
  [getAllStores],
  (stores) =>
    stores.filter(
      (store: Store) =>
        isPurchaseDetailStore(store) &&
        store.type !== "delete" &&
        !(store as any).is_migration_rebalance
    ) as PurchaseEvent[]
);

export const hasPurchaseStore = (state: RootState) =>
  getAllPurchases(state).length > 0 ? true : false;

export const hasTHRStore = (state: RootState) =>
  getAllTakeHomeRationsDeliveries(state).length > 0 ? true : false;

export const checkDeliveryDatetimeIsUniquePerDatetime = (
  datetime: Moment,
  currentStore: Store
) => (state: RootState) => {
  return !getAllStores(state).find(
    (store: Store) =>
      isDeliveryStore(store) &&
      store.type !== "delete" &&
      moment(store.occurred_at).isSame(datetime) &&
      store.client_id !== currentStore?.client_id
  );
};

export const getAllOfflineUsers = (state: RootState): OfflineUser[] =>
  getAllStores(state).filter(
    (store: Store) => store.model === "offlineuser" && store.type !== "delete"
  ) as OfflineUser[];

export const getOfflineUserByClientId = (id: string) => (state: RootState) =>
  getAllOfflineUsers(state).find((store: Store) => store.client_id === id);

export const getAllIncidents = createSelector(
  [getAllStores],
  (stores): IncidentEvent[] => stores.filter(isIncidentStore) as IncidentEvent[]
);

export const checkIncidentDateIsUniquePerDate = (
  datetime: string,
  currentStore: Store
) => (state: RootState) => {
  return !getAllStores(state).find(
    (store: Store) =>
      isIncidentStore(store) &&
      store.type !== "delete" &&
      !store.meta?.is_initial_stock_incident &&
      moment(store.occurred_at).isSame(datetime) &&
      store.client_id !== currentStore?.client_id
  );
};

export const getAllEnrolmentUpdates = createSelector(
  [getAllStores],
  (stores) =>
    stores.filter(
      (store: Store) => store.model === "enrolment"
    ) as EnrolmentUpdate[]
);

export const checkEnrolmentUpdateIsUniquePerDate = (
  date: string,
  currentStore: Store
) => (state: RootState) => {
  return !getAllStores(state).find(
    (store: Store) =>
      store.model === "enrolment" &&
      store.type !== "delete" &&
      moment(store.occurred_on).isSame(date) &&
      store.client_id !== currentStore?.client_id
  );
};

export const getEnrolmentUpdatesByDateRange = (
  date0: string,
  date1: string
) => (state: RootState) =>
  getAllEnrolmentUpdates(state).filter((enrolment) =>
    moment(enrolment.occurred_on).isBetween(date0, date1, "day", "[]")
  );

export const getAllSchoolYears = createSelector(
  [getAllStores],
  (stores): SchoolYear[] => stores.filter(isSchoolYearStore) as SchoolYear[]
);

export const getOngoingSchoolYearByDate = (date: moment.MomentInput) =>
  createSelector(
    [getAllSchoolYears],
    (schoolYears) =>
      schoolYears.find((schoolYear) =>
        // "[]" argument means "including first and last days"
        moment(date).isBetween(
          schoolYear.starts_on,
          schoolYear.ends_on,
          "days",
          "[]"
        )
      ) || null
  );

export const getSchoolYearById = (schoolYearId: number) =>
  createSelector([getAllSchoolYears], (schoolYears) =>
    schoolYears.find((schoolYear) => schoolYear.object_id === schoolYearId)
  );

export const getEnrolmentUpdatesBySchoolYearId = (schoolYearId: number) =>
  createSelector([getAllEnrolmentUpdates], (allEnrolmentUpdates) =>
    allEnrolmentUpdates.filter(
      (enrolmentUpdate) => enrolmentUpdate.school_year === schoolYearId
    )
  );

export const getSchoolYearLevelsBySchoolYearId = (schoolYearId: number) =>
  createSelector(
    [
      getSchoolYearById(schoolYearId),
      getEnrolmentUpdatesBySchoolYearId(schoolYearId),
    ],
    (schoolYear, enrolmentUpdates) => {
      const levels: {
        [key: string]: AggregateSchoolYearLevel;
      } = {};
      // Add all levels from schoolYear to the result
      if (schoolYear) {
        for (const schoolYearLevel of schoolYear.levels) {
          if (!(schoolYearLevel.level in levels)) {
            levels[schoolYearLevel.level] = schoolYearLevel;
          }
        }
      } else {
        // School Year does not exist just return empty array.
        return Object.values(levels);
      }

      for (const enrolmentUpdate of enrolmentUpdates) {
        for (const enrolmentUpdateLevel of enrolmentUpdate.levels) {
          if (!(enrolmentUpdateLevel.level in levels)) {
            levels[enrolmentUpdateLevel.level] = enrolmentUpdateLevel;
          }
        }
      }
      return Object.values(levels);
    }
  );

/**
 * Returns the "current" (see wiki) SchoolYear store
 */
export const getCurrentSchoolYear = (state: RootState) =>
  getOngoingSchoolYearByDate(moment().format("YYYY-MM-DD"))(state);

export const getPreviousSchoolYears = (state: RootState) => {
  const currentSchoolYear = getCurrentSchoolYear(state);
  const previousYears = getAllSchoolYears(state)
    .sort((a: SchoolYear, b: SchoolYear) =>
      moment(a.starts_on).isAfter(b.starts_on) ? -1 : 1
    )
    .filter((schoolYear: SchoolYear) =>
      moment(schoolYear.ends_on).isBefore(currentSchoolYear?.starts_on)
    );
  return previousYears;
};

/**
 * Returns last started school year. Before given date.
 * This date will be used to calculate stock values.
 *
 */
export const getLastStartedSchoolYear = (dateString: string) => (
  state: RootState
) => {
  const date = moment(dateString);
  const schoolYears = getAllSchoolYears(state).sort(compareByDate);
  return _.findLast(schoolYears, (year: SchoolYear) =>
    date.isSameOrAfter(getStoreDate(year))
  );
};

export const getCurrentOrLatestSchoolYear = (state: RootState) => {
  // If there is a current school year, return it
  // otherwise, return the latest previous school year or undefined
  const currentSchoolYear = getCurrentSchoolYear(state);
  const previousSchoolYears = getPreviousSchoolYears(state);
  return currentSchoolYear
    ? currentSchoolYear
    : !currentSchoolYear && previousSchoolYears?.length > 0
    ? previousSchoolYears[0]
    : undefined;
};

export const getSchoolYearsByYearAndMonth = (year: number, month: number) => (
  state: RootState
) => {
  const dateToCheck = moment()
    .year(year)
    .month(month - 1)
    .date(1);
  return getAllSchoolYears(state).filter((schoolYear) =>
    dateToCheck.isBetween(
      schoolYear?.starts_on,
      schoolYear?.ends_on,
      "month",
      "[]"
    )
  );
};

export const getSchoolYearByDate = (date: moment.MomentInput) => (
  state: RootState
) => getOngoingSchoolYearByDate(date)(state);

/**
 * Returns the "upcoming" (see wiki) SchoolYear store
 *
 * It's the SchoolYear right after the current one.
 */
export const getUpcomingSchoolYear = (state: RootState) => {
  const currentSchoolYear = getCurrentSchoolYear(state);
  const now = moment();
  const upcomingYear = getAllSchoolYears(state)
    .slice()
    .sort((a: SchoolYear, b: SchoolYear) =>
      moment(a.starts_on).isAfter(b.starts_on) ? 1 : -1
    )
    .find((schoolYear: SchoolYear) => {
      const isCurrentSchoolYear =
        currentSchoolYear &&
        currentSchoolYear.client_id === schoolYear.client_id;
      return now.isBefore(schoolYear.starts_on) && !isCurrentSchoolYear;
    });
  return upcomingYear || null;
};

/**
 * Returns EnrolmentUpdate stores within the "current" SchoolYear
 */
export const getCurrentSchoolYearEnrolmentUpdates = createSelector(
  [getCurrentSchoolYear, getAllEnrolmentUpdates],
  (currentSchoolYear, enrolmentUpdates) => {
    if (currentSchoolYear === null) return [];
    return enrolmentUpdates.filter((enrolmentUpdate: { occurred_on: string }) =>
      moment(enrolmentUpdate.occurred_on).isBetween(
        currentSchoolYear.starts_on,
        currentSchoolYear.ends_on,
        "day",
        "[]"
      )
    );
  }
);

/**
 * Returns a Moment instance of when a syncable "happened".
 * In the case of school years, when a school year started.
 */
export const getSyncableMoment = (s: Store): Moment => {
  // StoreEvent types use event_type + occurred_at
  if ("event_type" in s && s.event_type) {
    return moment(s.occurred_at);
  }
  switch (s.model) {
    case "attendance":
      return moment(s.occurred_on);
    case "delivery":
      return moment((s as any).occurred_at);
    case "purchasedetail":
      return moment((s as any).occurred_at);
    case "enrolment":
      return moment(s.occurred_on);
    case "incident":
      return moment(s.occurred_at);
    case "year":
      return moment(s.starts_on);
    case "offlineuser":
      return moment();
    default:
      return moment();
  }
};

/** Function to be used as an argument to sort(). Sorts by decreasing datetime */
export const syncableSortFnDecDateTime = (a: Store, b: Store) =>
  getSyncableMoment(b).valueOf() - getSyncableMoment(a).valueOf();

export const getIsSyncableLocked = (state: RootState, syncable: Store) => {
  if (!syncable) return false;
  const syncableMoment = getSyncableMoment(syncable);
  const report = getReportByMoment(state, syncableMoment);
  return getIsReportLocked(report);
};

export const getIsDateLocked = (
  state: RootState,
  date: Moment | MomentInput
) => {
  if (!date) return false;
  const report = getReportByMoment(state, date);
  return getIsReportLocked(report);
};

export const getConsumptionNoMealReasons = (
  consumption: Attendance["consumption"]
): number[] =>
  consumption?.no_meal_reasons?.length
    ? consumption.no_meal_reasons
    : consumption?.meta?.no_meal_reasons ?? [];

export const isAttendanceStoreCompleted = (
  attendanceStore: Attendance,
  studentAttendanceStore: Attendance,
  currentSchoolEnabledStudentAttendance: boolean,
  idToNoMealReasonMapping: IdToNoMealReasonMapping
) => {
  let isCompleted = false;

  const noMealReasons = getConsumptionNoMealReasons(
    attendanceStore?.consumption
  );
  const hasNoMealReason = noMealReasons.length > 0;

  const isNoSchoolDay =
    hasNoMealReason &&
    noMealReasons.some((rId) => idToNoMealReasonMapping[rId]?.is_no_school_day);

  const isNoMealDay = hasNoMealReason && !isNoSchoolDay;

  const hasConsumption = !!attendanceStore?.consumption?.meal_provided;

  const hasMealAttendance = (attendanceStore.levels || []).length > 0;

  if (currentSchoolEnabledStudentAttendance) {
    // Checks when student attendance is enabled
    // This is reversed so I would not need to implement seperate studentattendanceEnable=False cases.
    const missingStudentAttendance = studentAttendanceStore === undefined;

    // Calculate complete days:
    const isFullyComplete =
      !hasNoMealReason &&
      hasConsumption &&
      hasMealAttendance &&
      !missingStudentAttendance;

    const isNoSchoolDayComplete =
      isNoSchoolDay &&
      !hasConsumption &&
      !hasMealAttendance &&
      missingStudentAttendance;

    const isNoMealDayComplete =
      isNoMealDay &&
      !hasConsumption &&
      !hasMealAttendance &&
      !missingStudentAttendance;

    isCompleted =
      isFullyComplete || isNoSchoolDayComplete || isNoMealDayComplete;
  } else {
    // Normal flow checks
    const isFullyComplete =
      !hasNoMealReason && hasConsumption && hasMealAttendance;

    const isNoSchoolDayComplete =
      isNoSchoolDay && !hasConsumption && !hasMealAttendance;

    const isNoMealDayComplete =
      isNoMealDay && !hasConsumption && hasMealAttendance;

    isCompleted =
      isFullyComplete || isNoSchoolDayComplete || isNoMealDayComplete;
  }

  return isCompleted;
};

export const sidebarStyling = {
  complete: { backgroundColor: "#c1eed099" },
  incomplete: {
    backgroundColor: "#f6e6e9",
  },
};

export const attendanceStoreSidebarItemStyling = (
  attendanceStore: Attendance,
  studentAttendanceStore: Attendance,
  currentSchoolEnabledStudentAttendance: boolean,
  idToNoMealReasonMapping: IdToNoMealReasonMapping
) => {
  if (attendanceStore?.model === "attendance") {
    if (attendanceStore?.category === deliveryCategory) {
      return isAttendanceStoreCompleted(
        attendanceStore,
        studentAttendanceStore,
        currentSchoolEnabledStudentAttendance,
        idToNoMealReasonMapping
      )
        ? sidebarStyling.complete
        : sidebarStyling.incomplete;
    } else if (!attendanceStore?.category) {
      // if model === attendance and there is no category,
      // it seems to indicate that there is no meal or attendance on
      // that particular date.
      return sidebarStyling.incomplete;
    } else {
      // If it is takehome ration or student attendance just show it as green.
      return sidebarStyling.complete;
    }
  } else {
    return {};
  }
};

//
// UTILS
//

const filterUnsyncedStores = (stores: Store[] = []) =>
  stores.filter((store: { sync: boolean }) => store.sync !== true);

const getUserStoresChanged = (
  state: StoresState,
  newStoresArray: Store[],
  action: ActionState
) => {
  // Logic for handling the persistance of the changes made by offline admin
  // users accross multiple schools.

  const { user, school } = action;

  // At the moment it will only work if the userStores is already populated
  // (i.e. an admin with offline capability logs in online)
  let userStoresChanged: { userStores?: UserStores } = {};
  const userStoresKeys = Object.keys(state.userStores || {});
  if (school?.id && getCanUseOfflineMode(user) && userStoresKeys.length) {
    userStoresChanged = {
      userStores: { ...state.userStores, [school.id]: newStoresArray },
    };
  }
  return userStoresChanged;
};
